/** @format */

import React, { FC, useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import G6 from '@antv/g6'
import { TopoLayoutType } from './index'
import NodeDrawer from './NodeDrawer'
import EdgeDrawer from './EdgeDrawer'
import useSize from 'src/hooks/useSize'
import ToolbarUpdate from 'src/containers/MonitorCloud/IbTopology/icon/toolbar_update.png'
import ToolbarDefault from 'src/containers/MonitorCloud/IbTopology/icon/toolbar_default.png'
import ToolbarRatio from 'src/containers/MonitorCloud/IbTopology/icon/toolbar_ratio.png'
import ToolbarFull from 'src/containers/MonitorCloud/IbTopology/icon/toolbar_full.png'
import ToolbarExitFull from 'src/containers/MonitorCloud/IbTopology/icon/toolbar_exitfull.png'
import ToolbarAmplify from 'src/containers/MonitorCloud/IbTopology/icon/toolbar_amplify.png'
import ToolbarReduce from 'src/containers/MonitorCloud/IbTopology/icon/toolbar_reduce.png'
import variable from 'src/static/styles/var'
import { useToggle, useFullscreen, useDebounce } from 'react-use'
import { nodeImg, nodeHoverImg, nodeActiveImg } from './constants'

interface TopologyProps {
  className?: string
  updateGraph: () => void
  nodelist: any[]
  edgelist: any[]
  layout: TopoLayoutType
  searchNodes: string[]
  dataSource: string
}

const Topology: FC<TopologyProps> = ({
  className,
  updateGraph,
  nodelist,
  edgelist,
  dataSource,
  searchNodes,
  layout
}) => {
  // const graphRef = useRef<any>(null)
  const graphRef = React.useRef<any>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [nodes, setNodes] = useState<any[]>([])
  const [nodeVisible, setNodeVisible] = useState<boolean>(false)
  const [nodeData, setNodeData] = useState<any>()
  const [edgeVisible, setEdgeVisible] = useState<boolean>(false)
  const [edgeData, setEdgeData] = useState<any>()
  // 全屏
  const fullRef = useRef(null)
  const [show, toggle] = useToggle(false)
  const isFullscreen = useFullscreen(fullRef, show, { onClose: () => toggle(false) })

  // 初始化graph
  useEffect(() => {
    const graph = new G6.Graph({
      container: containerRef?.current,
      plugins: [],
      fitView: true,
      fitViewPadding: [20, 20, 20, 20],
      linkCenter: true,
      maxZoom: 2,
      minZoom: 0.25,
      animate: false,
      modes: {
        default: [
          'drag-node',
          'zoom-canvas',
          'drag-canvas',
          {
            type: 'click-select',
            multiple: false,
            selectEdge: true
          },
          'activate-relations'
        ]
      },
      defaultNode: {
        type: 'image',
        size: [62, 64],
        labelCfg: {
          offset: 0,
          style: {
            fontSize: 12
          }
        },
        style: {
          cursor: 'pointer'
        }
      },
      defaultEdge: {
        type: 'line',
        style: {
          lineWidth: 1,
          cursor: 'pointer'
        }
      },
      edgeStateStyles: {
        hover: {
          lineWidth: 2
        },
        selected: {
          lineWidth: 2
        },
        active: {
          lineWidth: 1
        }
      }
    })
    // 点击设备
    graph.on('node:click', (e: any) => {
      const node = e.item
      const data = node?.getModel()
      if (data) {
        setNodeData(data)
        setNodeVisible(true)
      }
    })
    // 点击链路
    graph.on('edge:click', (e: any) => {
      const edge = e.item
      const data = edge?.getModel()
      if (data) {
        setEdgeData(data)
        setEdgeVisible(true)
      }
    })

    // node-hover
    graph.on('node:mouseenter', e => {
      const node = e.item
      node && graph.setItemState(node, 'hover', true)
    })
    graph.on('node:mouseleave', e => {
      const node = e.item
      node && graph.setItemState(node, 'hover', false)
    })

    // edge-hover
    graph.on('edge:mouseenter', e => {
      const edge = e.item
      edge && graph.setItemState(edge, 'hover', true)
    })

    graph.on('edge:mouseleave', e => {
      const edge = e.item
      edge && graph.setItemState(edge, 'hover', false)
    })

    graphRef.current = graph
  }, [])

  // 生成nodes、edges && 渲染
  useEffect(() => {
    if (graphRef?.current) {
      // 设备
      const x_position = {}
      const nodes_map =
        nodelist?.map(item => {
          const { DN, NeName, y_level, FabricNeRoles } = item
          if (x_position[y_level] === undefined) {
            x_position[y_level] = 0
          }
          const wrap = Math.floor(x_position[y_level] / 20)
          const config = {
            id: DN,
            label: NeName,
            FabricNeRoles,
            style: {
              img: searchNodes.includes(DN) ? nodeActiveImg[FabricNeRoles] : nodeImg[FabricNeRoles],
              'node-label': {}
            },
            labelCfg: {
              style: {
                fill: '#0091ea',
                fontSize: 12,
                offset: 0
              }
            },
            x: (x_position[y_level] % 20) * 120,
            y:
              x_position[y_level] % 2 === 1
                ? y_level * 300 + wrap * 100
                : y_level * 300 + 50 + wrap * 100,
            y_level,
            stateStyles: {
              hover: {
                img: nodeHoverImg[FabricNeRoles]
              },
              selected: {
                img: nodeActiveImg[FabricNeRoles]
              },
              inactive: {
                opacity: 0.75,
                'node-label': {
                  opacity: 0.75
                }
              }
            },
            param: {
              ...item
            }
          }
          x_position[y_level] += 1
          return config
        }) || []
      setNodes(nodes_map)
      // 链路
      const edges_map =
        edgelist?.map(({ edge_id, links }) => {
          const [source_guid, destination_guid] = edge_id.split('===')
          const config = {
            source: source_guid,
            target: destination_guid,
            id: edge_id,
            links,
            style: {
              stroke: '#3A3A3A'
            }
          }
          return config
        }) || []
      // 过滤没有设备的线
      const edges_filter = edges_map?.filter(edge => {
        const { source, target } = edge
        return (
          !!nodes_map?.find(({ id }) => id === source) &&
          !!nodes_map?.find(({ id }) => id === target)
        )
      })
      graphRef?.current.read({ nodes: nodes_map, edges: edges_filter })
    }
  }, [graphRef, nodelist, edgelist, searchNodes])

  // Graph plugins
  useEffect(() => {
    if (containerRef?.current && graphRef?.current) {
      const minimap = new G6.Minimap({
        className: 'g6-minimap',
        size: [198, 88.38],
        fill: '#fff'
      })
      const toolbar = new G6.ToolBar({
        className: 'g6-toolbar-ul',
        getContent: () => {
          return `
          <ul>
            <li code='update' title="刷新">
              <img src=${ToolbarUpdate} />
            </li>
            <li code='init' title="默认布局">
              <img src=${ToolbarDefault} />
            </li>
            <li code='full' title="全屏">
              <img src=${isFullscreen ? ToolbarExitFull : ToolbarFull} />
            </li>
            <li code='ratio' title="等比布局">
              <img src=${ToolbarRatio} />
            </li>
            <li code='amplify' title="放大">
              <img src=${ToolbarAmplify} />
            </li>
            <li code='reduce' title="缩小">
              <img src=${ToolbarReduce} />
            </li>
          </ul>
        `
        },
        handleClick: (code, graph) => {
          if (code === 'update') {
            updateGraph()
          } else if (code === 'init') {
            layout === 'disperse' ? graph?.render() : levelLayout()
          } else if (code === 'full') {
            toggle()
          } else if (code === 'ratio') {
            graph?.zoomTo(1)
          } else if (code === 'amplify') {
            const zoom = graph?.getZoom()
            graph?.zoomTo(zoom + 0.1)
          } else if (code === 'reduce') {
            const zoom = graph?.getZoom()
            graph?.zoomTo(zoom - 0.1)
          }
        }
      })
      const canvas = containerRef?.current?.getElementsByTagName('canvas')[0]
      containerRef.current.innerHTML = ''
      containerRef.current.appendChild(canvas)
      graphRef.current?.set('plugins', [toolbar, minimap])
      graphRef.current.initPlugins()
    }
  }, [containerRef, graphRef, isFullscreen, dataSource, layout, updateGraph])

  // 关闭抽屉
  const onClose = () => {
    graphRef?.current.getNodes().forEach(node => {
      graphRef?.current.clearItemStates(node, 'selected')
    })
    graphRef?.current
      .getEdges()
      .forEach(edge => graphRef?.current.clearItemStates(edge, 'selected'))
    setNodeVisible(false)
    setNodeData(undefined)
    setEdgeVisible(false)
    setEdgeData(undefined)
  }

  // 切换布局
  useEffect(() => {
    if (graphRef?.current) {
      if (layout === 'disperse') {
        graphRef?.current?.updateLayout({
          type: 'radial',
          animate: false,
          linkDistance: 200,
          preventOverlap: true,
          nodeSize: 100
        })
        graphRef?.current?.render()
      } else if (layout === 'role') {
        graphRef?.current?.destroyLayout()
        levelLayout()
      }
    }
  }, [layout])

  // 分层布局，默认位置
  const levelLayout = () => {
    if (graphRef?.current) {
      // 设备
      const x_position = {}
      graphRef?.current.getNodes().forEach(node => {
        const model = node.getModel()
        const { y_level } = model
        if (x_position[y_level] === undefined) {
          x_position[y_level] = 3
        }
        const wrap = Math.floor(x_position[y_level] / 20)
        node.update({
          ...model,
          x: (x_position[y_level] % 20) * 120,
          y:
            x_position[y_level] % 2 === 1
              ? y_level * 300 + wrap * 100
              : y_level * 300 + 50 + wrap * 100
        })
        x_position[y_level] += 1
      })
      graphRef?.current?.render()
    }
  }

  // 画布大小变化
  const { width, height } = useSize(containerRef)
  useDebounce(
    () => {
      if (graphRef?.current) {
        graphRef.current.changeSize(width, height)
        graphRef.current.fitView()
        document
          .getElementsByClassName('g6-legend-container')?.[0]
          ?.setAttribute('style', 'position: absolute; left: 8px; bottom:0px;')
      }
    },
    200,
    [width, height]
  )

  return (
    <div className={className} ref={fullRef}>
      {isFullscreen ? <div className="loading-mask monitor-loading"></div> : null}
      <div className="content" style={{ width: '100%', height: '100%' }} ref={containerRef}></div>
      <NodeDrawer
        visible={nodeVisible}
        onClose={onClose}
        dataSource={dataSource}
        nodeData={nodeData}
      />
      <EdgeDrawer
        visible={edgeVisible}
        onClose={onClose}
        dataSource={dataSource}
        edgeData={edgeData}
        nodes={nodes}
      />
    </div>
  )
}

export default styled(Topology)`
  margin-top: 16px;
  height: calc(100vh - 296px);
  background-color: #fafbff;
  position: relative;
  .g6-minimap {
    position: absolute;
    top: 46px;
    right: 8px;
    background: #fff;
    border: 1px solid ${variable.primaryColor};
  }
  .g6-toolbar-ul {
    position: absolute;
    top: 8px;
    right: 24px;
    display: flex;
    align-items: center;
    border-radius: 2px;
    cursor: pointer;
    li {
      padding: 6px 6px;
      background: #fff;
      &:hover {
        background: #eee;
      }
    }
  }
`
